import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { customerService } from '../../services/customerService';
import api from '../../services/api';

const Customers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [customerForm, setCustomerForm] = useState({
    customer_type: 'individual',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    alternate_phone: '',
    date_of_birth: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    company_name: '',
    tax_id: '',
    preferred_contact_method: 'email',
    sales_rep: '',
  });

  useEffect(() => {
    fetchCustomers();
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    if (searchParam && searchParam !== search) {
      setSearch(searchParam);
    }
  }, [location.search]);

  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
      };
      if (search) {
        params.search = search;
      }
      
      const response = await customerService.getCustomers(params);
      setCustomers(response.results || []);
      setTotal(response.count || 0);
    } catch (err) {
      setError('Error loading customers: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setCustomerForm({
        customer_type: customer.customer_type || 'individual',
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        alternate_phone: customer.alternate_phone || '',
        date_of_birth: customer.date_of_birth || '',
        address_line1: customer.address_line1 || '',
        address_line2: customer.address_line2 || '',
        city: customer.city || '',
        state: customer.state || '',
        zip_code: customer.zip_code || '',
        country: customer.country || 'USA',
        company_name: customer.company_name || '',
        tax_id: customer.tax_id || '',
        preferred_contact_method: customer.preferred_contact_method || 'email',
        sales_rep: customer.sales_rep || '',
      });
    } else {
      setEditingCustomer(null);
      setCustomerForm({
        customer_type: 'individual',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        alternate_phone: '',
        date_of_birth: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'USA',
        company_name: '',
        tax_id: '',
        preferred_contact_method: 'email',
        sales_rep: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCustomer(null);
    setFieldErrors({});
  };

  const handleSave = async () => {
    try {
      setFieldErrors({});

      // Clean the form data - convert empty date string to null
      const cleanedData = {
        ...customerForm,
        date_of_birth: customerForm.date_of_birth || null,
      };

      if (editingCustomer) {
        await customerService.updateCustomer(editingCustomer.id, cleanedData);
      } else {
        await customerService.createCustomer(cleanedData);
      }
      fetchCustomers();
      handleCloseDialog();
    } catch (err) {
      // Check if error response has field-level validation errors
      if (err.response?.data && typeof err.response.data === 'object') {
        const errors = err.response.data;
        // Check if it's a field error object (has array values)
        const hasFieldErrors = Object.values(errors).some(val => Array.isArray(val));
        if (hasFieldErrors) {
          setFieldErrors(errors);
          return; // Don't set general error if we have field errors
        }
      }
      setError('Error saving customer: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (customerId) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await customerService.deleteCustomer(customerId);
        fetchCustomers();
      } catch (err) {
        setError('Error deleting customer: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleSendSMS = async (customer) => {
    if (!customer.phone) {
      setError('Customer has no phone number');
      return;
    }

    try {
      // Start conversation with customer's phone
      const response = await api.post('/v1/conversations/conversations/start_conversation/', {
        phone_number: customer.phone,
        contact_name: customer.full_name,
      });

      // Store conversation ID and navigate to chat
      sessionStorage.setItem('chat:openConversationId', response.data.conversation.id);
      navigate('/chat');
    } catch (err) {
      setError('Error starting SMS conversation: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleFormChange = (field) => (event) => {
    setCustomerForm({
      ...customerForm,
      [field]: event.target.value,
    });
  };

  if (loading && customers.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Customer Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Customer
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" mb={2}>
        <TextField
          variant="outlined"
          placeholder="Search customers..."
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ minWidth: 300 }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Sales Rep</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((customer) => (
              <TableRow key={customer.id}>
                <TableCell>{customer.full_name}</TableCell>
                <TableCell>
                  <Chip
                    label={customer.customer_type}
                    variant="outlined"
                    size="small"
                    color={customer.customer_type === 'business' ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>{customer.email}</TableCell>
                <TableCell>{customer.phone}</TableCell>
                <TableCell>{customer.city}</TableCell>
                <TableCell>{customer.sales_rep_name || 'Unassigned'}</TableCell>
                <TableCell>
                  {customer.phone && (
                    <IconButton
                      onClick={() => handleSendSMS(customer)}
                      color="success"
                      title="Send SMS"
                    >
                      <SmsIcon />
                    </IconButton>
                  )}
                  <IconButton onClick={() => handleOpenDialog(customer)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(customer.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Customer Type</InputLabel>
                <Select
                  value={customerForm.customer_type}
                  label="Customer Type"
                  onChange={handleFormChange('customer_type')}
                >
                  <MenuItem value="individual">Individual</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Preferred Contact</InputLabel>
                <Select
                  value={customerForm.preferred_contact_method}
                  label="Preferred Contact"
                  onChange={handleFormChange('preferred_contact_method')}
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="phone">Phone</MenuItem>
                  <MenuItem value="sms">SMS</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={customerForm.first_name}
                onChange={handleFormChange('first_name')}
                required
                error={!!fieldErrors.first_name}
                helperText={fieldErrors.first_name?.[0]}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={customerForm.last_name}
                onChange={handleFormChange('last_name')}
                required
                error={!!fieldErrors.last_name}
                helperText={fieldErrors.last_name?.[0]}
              />
            </Grid>
            {customerForm.customer_type === 'business' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={customerForm.company_name}
                    onChange={handleFormChange('company_name')}
                    error={!!fieldErrors.company_name}
                    helperText={fieldErrors.company_name?.[0]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Tax ID"
                    value={customerForm.tax_id}
                    onChange={handleFormChange('tax_id')}
                    error={!!fieldErrors.tax_id}
                    helperText={fieldErrors.tax_id?.[0]}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={customerForm.email}
                onChange={handleFormChange('email')}
                required
                error={!!fieldErrors.email}
                helperText={fieldErrors.email?.[0]}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={customerForm.phone}
                onChange={handleFormChange('phone')}
                required
                error={!!fieldErrors.phone}
                helperText={fieldErrors.phone?.[0]}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Alternate Phone"
                value={customerForm.alternate_phone}
                onChange={handleFormChange('alternate_phone')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={customerForm.date_of_birth}
                onChange={handleFormChange('date_of_birth')}
                InputLabelProps={{ shrink: true }}
                error={!!fieldErrors.date_of_birth}
                helperText={fieldErrors.date_of_birth?.[0]}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={customerForm.address_line1}
                onChange={handleFormChange('address_line1')}
                required
                error={!!fieldErrors.address_line1}
                helperText={fieldErrors.address_line1?.[0]}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={customerForm.address_line2}
                onChange={handleFormChange('address_line2')}
                error={!!fieldErrors.address_line2}
                helperText={fieldErrors.address_line2?.[0]}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={customerForm.city}
                onChange={handleFormChange('city')}
                required
                error={!!fieldErrors.city}
                helperText={fieldErrors.city?.[0]}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                value={customerForm.state}
                onChange={handleFormChange('state')}
                required
                error={!!fieldErrors.state}
                helperText={fieldErrors.state?.[0]}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={customerForm.zip_code}
                onChange={handleFormChange('zip_code')}
                required
                error={!!fieldErrors.zip_code}
                helperText={fieldErrors.zip_code?.[0]}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingCustomer ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;
